import WebCryptoWrapper from './WebCryptoWrapper';
import ArrayHelper from '../helpers/ArrayHelper';

class WebCryptoAesCbc {
	static isSupportedAsync() {
		return WebCryptoWrapper.isSupportedAsync();
	}

	/**
	 * Initialises the WebCrypto sub-system. Only one instance is needed.
	 */
	constructor() {
		this.crypto = new WebCryptoWrapper();
	}

	/**
	 * returns an AES-CBC cryptoKey from Uint8Array(16) for use with decrypt
	 */
	createKey( key ) {
		try {
			if( key.length !== 16 ) {
				throw new Error( 'WebCryptoAesCbc.createKey length must be 16');
			}

			return this.crypto.importKey(
				'raw',
				key,
				{ name: 'AES-CBC' },
				false,
				['encrypt', 'decrypt']
			);
		}
		catch(err) {
			return Promise.reject(err);
		}
	}

	_padCypherTextAsync( cryptoKey, cypherText ) {

		// the padding needs to be encoded with an IV which is the last
		// 16 bytes of the current cypherText
		let paddingIv = cypherText.subarray( cypherText.length - 16 );

		// the padding consists of 16 bytes of 16. In pkcs7, 16 indicates to remove 16 bytes,
		// ie strip all the padding.
		let padding = new Uint8Array(16).fill(16);

		// now we need to encrypt the padding
		return this.crypto.encrypt(
			{
				name: 'AES-CBC',
				iv : paddingIv,
			},
			cryptoKey,
			padding )
		.then( encrypted => {
			// encrypted is an ArrayBuffer, we need just the first 16 bytes
			let encryptedPadding = new Uint8Array( encrypted, 0, 16 );

			// we need to allocate a new array which includes space for the extra 16 bytes
			// of encrypted padding
			let paddedCypherText = new Uint8Array( cypherText.length + 16 );

			// now we concat the two data items together
			paddedCypherText.set( cypherText );
			paddedCypherText.set( encryptedPadding, cypherText.length );

			return paddedCypherText;
		});
	}

	_decryptInternal( cryptoKey, iv, cypherText) {
		return this.crypto.decrypt(
			{
				name: 'AES-CBC',
				iv,
			},
			cryptoKey,
			cypherText );
	}

	decrypt( cryptoKey, iv, cypherText ) {
		try{
			// we need to use a Uint8Array for some of our processing
			let myCypherText = ArrayHelper.makeUint8Array(cypherText);

			// if we're non-modulo 16 truncate if necessary
			let truncated;
			({ data: myCypherText, truncated } = ArrayHelper.makeModulo16(myCypherText));

			// we need to add padding if we truncated
			if( truncated ) {
				// adding padding involves encrypting, and is an Async op
				// thus myCypherText is now a promise.
				myCypherText = this._padCypherTextAsync( cryptoKey, myCypherText);
			}

			// because myCypherText might be a promise, we need to resolve it
			return Promise.resolve(myCypherText)
			.then( resolvedCypherText => {
				return this._decryptInternal( cryptoKey, iv, resolvedCypherText )
				.catch( err => {

					// if we get here, the decrypt failed... if we have't already truncated
					// and added padding, then we assume it failed because it was truncated
					// to modulo-16, thus we try again, but with added padding.
					if( !truncated ) {
						return this._padCypherTextAsync( cryptoKey, resolvedCypherText)
						.then( paddedCypherText => this._decryptInternal( cryptoKey, iv, paddedCypherText ) );

						// if this fails, we can't resolve it, and the client will need to
						// deal with the failed promise.
					}
					else {
						// this means that we failed after truncating, which shouldn't happen.
						throw err;
					}
				});
			});
		}
		catch(err) {
			return Promise.reject(err);
		}
	}


}

export default WebCryptoAesCbc;



// WEBPACK FOOTER //
// ./src/aes/WebCryptoAesCbc.js